import { getSharedAudioContext } from "./iosAudioUnlock";
import { parseMouthCueTrack, type MouthCueTrack } from "./timedMouthCues";

export const SPEECH_ENVELOPE_HOP_MS = 16;
const MAX_AUDIO_BYTES = 12 * 1024 * 1024;
const DECODE_TIMEOUT_MS = 2000;
export interface AuthoredSpeechClip {
  text: string;
  language: string;
  url: string;
  mouthCues: MouthCueTrack;
}
export interface PreparedSpeechAudio {
  url: string;
  envelope: Float32Array | null;
  mouthCues: MouthCueTrack | null;
  release: () => void;
}
const preparedByElement = new WeakMap<HTMLAudioElement, PreparedSpeechAudio>();
export function bindPreparedSpeech(audio: HTMLAudioElement, prepared: PreparedSpeechAudio) {
  preparedByElement.set(audio, prepared);
}
export function clearPreparedSpeech(audio: HTMLAudioElement) { preparedByElement.delete(audio); }
export function getPreparedSpeech(audio: HTMLAudioElement): PreparedSpeechAudio | null {
  const prepared = preparedByElement.get(audio);
  return prepared && audio.src === prepared.url ? prepared : null;
}

export function computeSpeechEnvelope(buffer: Pick<AudioBuffer, "sampleRate" | "getChannelData">): Float32Array {
  const samples = buffer.getChannelData(0);
  const hop = Math.max(1, Math.round(buffer.sampleRate * SPEECH_ENVELOPE_HOP_MS / 1000));
  const rms = new Float32Array(Math.ceil(samples.length / hop));
  for (let i = 0; i < rms.length; i++) {
    const start = i * hop, end = Math.min(samples.length, start + hop);
    let sum = 0;
    for (let j = start; j < end; j++) sum += samples[j] * samples[j];
    rms[i] = Math.sqrt(sum / (end - start));
  }
  return rms;
}
function assertActive(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException("Speech cancelled", "AbortError");
}
async function decodeEnvelope(bytes: ArrayBuffer): Promise<Float32Array | null> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const context = getSharedAudioContext();
    if (!context) return null;
    return await Promise.race([
      context.decodeAudioData(bytes.slice(0)).then(computeSpeechEnvelope).catch(() => null),
      new Promise<null>(resolve => { timeout = setTimeout(() => resolve(null), DECODE_TIMEOUT_MS); }),
    ]);
  } catch {
    // Some browsers throw synchronously instead of rejecting decodeAudioData.
    // Optional acting analysis must never reject otherwise valid native audio.
    return null;
  } finally { clearTimeout(timeout); }
}
async function fetchAudioBytes(url: string, signal?: AbortSignal) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, 12_000);
  try {
    assertActive(signal);
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error("Speech audio unavailable");
    if (Number(response.headers.get("content-length")) > MAX_AUDIO_BYTES) throw new Error("Speech audio too large");
    const bytes = await response.arrayBuffer();
    if (!bytes.byteLength || bytes.byteLength > MAX_AUDIO_BYTES) throw new Error("Invalid speech audio");
    assertActive(signal);
    return { bytes, contentType: response.headers.get("content-type") || "audio/mpeg" };
  } finally {
    clearTimeout(timer); signal?.removeEventListener("abort", abort);
  }
}

/** Fetch and analyse once BEFORE play; the same bytes drive native media and cues.
 * Analysis failure may degrade dynamic speech to native playback, but never
 * accepts an authored cue track for different audio.
 */
export async function prepareSpeechAudio(
  url: string, signal: AbortSignal, rawTrack?: unknown,
): Promise<PreparedSpeechAudio> {
  assertActive(signal);
  const track = rawTrack === undefined ? null : parseMouthCueTrack(rawTrack);
  if (rawTrack !== undefined && !track) throw new Error("Invalid speech mouth cues");
  let fetched: Awaited<ReturnType<typeof fetchAudioBytes>>;
  try { fetched = await fetchAudioBytes(url, signal); }
  catch (error) {
    assertActive(signal);
    if (track) throw error;
    // CORS/network analysis failure must not break a playable native-media URL.
    return { url, envelope: null, mouthCues: null, release: () => {} };
  }
  const { bytes, contentType } = fetched;
  if (track) {
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    const hash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
    if (hash !== track.audioSha256) throw new Error("Speech mouth cues do not match audio");
  }
  // Cues own the mouth; the same audio's envelope drives only phrase-level
  // head/brow acting. Loudness is never promoted to a phoneme timeline.
  const envelope = await decodeEnvelope(bytes);
  assertActive(signal);
  const blobUrl = URL.createObjectURL(new Blob([bytes], { type: contentType }));
  let released = false;
  return { url: blobUrl, envelope, mouthCues: track, release() {
    if (!released) { released = true; URL.revokeObjectURL(blobUrl); }
  } };
}

// Other voice consumers still pass native URLs. Bounded cache avoids retaining
// every sentence for the lifetime of the app; prepared Idiomas clips bypass it.
const envelopeCache = new Map<string, Float32Array>();
export async function readSpeechEnvelope(url: string): Promise<Float32Array | null> {
  const cached = envelopeCache.get(url);
  if (cached) return cached;
  try {
    const { bytes } = await fetchAudioBytes(url);
    const envelope = await decodeEnvelope(bytes);
    if (envelope) {
      if (envelopeCache.size >= 24) envelopeCache.delete(envelopeCache.keys().next().value!);
      envelopeCache.set(url, envelope);
    }
    return envelope;
  } catch { return null; }
}
